import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/context/LanguageContext";

/* Illustrazioni stilizzate: sciatori geometrici, nessuna foto */
function ViSvg() {
  return (
    <svg viewBox="0 0 200 160" className="h-full w-full" aria-hidden="true">
      {/* scia lasciata dalla coppia */}
      <path d="M18 138 Q70 122 118 128 T186 120" stroke="#1E293B" strokeWidth="5" fill="none" strokeLinecap="round" />
      {/* guida davanti (ambra): piegata in avanti, bastoncini */}
      <g stroke="#F59E0B" strokeWidth="6" strokeLinecap="round" fill="none">
        <circle cx="58" cy="30" r="9" fill="#F59E0B" stroke="none" />
        <path d="M56 40 L46 66" />
        <path d="M52 48 L34 70" strokeWidth="4.5" />
        <path d="M34 70 L28 96" strokeWidth="3.5" />
        <path d="M46 66 L60 82 L54 100" />
        <path d="M34 108 L76 100" strokeWidth="4" />
      </g>
      {/* onde sonore: la voce della guida */}
      <g stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" fill="none" opacity="0.75">
        <path d="M74 46 q9 7 0 15" />
        <path d="M83 39 q14 14 0 28" />
      </g>
      {/* atleta ipovedente che insegue (azzurro) */}
      <g stroke="#38BDF8" strokeWidth="6" strokeLinecap="round" fill="none">
        <circle cx="128" cy="46" r="9" fill="#38BDF8" stroke="none" />
        <path d="M126 56 L116 82" />
        <path d="M122 64 L104 86" strokeWidth="4.5" />
        <path d="M104 86 L98 112" strokeWidth="3.5" />
        <path d="M116 82 L130 98 L124 116" />
        <path d="M104 124 L146 116" strokeWidth="4" />
      </g>
    </svg>
  );
}

function StandingSvg() {
  return (
    <svg viewBox="0 0 200 160" className="h-full w-full" aria-hidden="true">
      <path d="M34 136 Q100 118 166 128" stroke="#1E293B" strokeWidth="5" fill="none" strokeLinecap="round" />
      {/* sciatore monogamba con stabilizzatori (outrigger) */}
      <g stroke="#34D399" strokeWidth="6" strokeLinecap="round" fill="none">
        <circle cx="98" cy="28" r="9" fill="#34D399" stroke="none" />
        <path d="M98 38 L90 66" />
        {/* unica gamba piegata sullo sci */}
        <path d="M90 66 L104 84 L96 104" />
        <path d="M72 112 L128 104" strokeWidth="4.5" />
        {/* braccia con outrigger: asta + mini sci */}
        <path d="M92 48 L72 66" strokeWidth="4.5" />
        <path d="M72 66 L52 104" strokeWidth="3.5" />
        <path d="M44 112 L62 107" strokeWidth="3.5" />
        <path d="M98 50 L118 68" strokeWidth="4.5" />
        <path d="M118 68 L140 100" strokeWidth="3.5" />
        <path d="M134 108 L150 103" strokeWidth="3.5" />
      </g>
    </svg>
  );
}

function SittingSvg() {
  return (
    <svg viewBox="0 0 200 160" className="h-full w-full" aria-hidden="true">
      <path d="M28 140 Q100 122 172 130" stroke="#1E293B" strokeWidth="5" fill="none" strokeLinecap="round" />
      {/* sciatore nel mono-sci */}
      <g stroke="#A78BFA" strokeWidth="6" strokeLinecap="round" fill="none">
        <circle cx="88" cy="26" r="9" fill="#A78BFA" stroke="none" />
        <path d="M88 36 L84 58" />
        {/* gambe raccolte in avanti */}
        <path d="M86 58 L112 66 L124 78" strokeWidth="5" />
        {/* seduta (bucket) */}
        <path d="M66 62 L104 62 L100 84 L70 84 Z" strokeWidth="5" />
        {/* ammortizzatore */}
        <path d="M80 84 L76 98 M92 84 L86 98" strokeWidth="3.5" />
        {/* sci singolo */}
        <path d="M52 108 L142 98" strokeWidth="5" />
        {/* outrigger */}
        <path d="M82 44 L62 62" strokeWidth="4.5" />
        <path d="M62 62 L44 96" strokeWidth="3.5" />
        <path d="M36 103 L54 99" strokeWidth="3.5" />
        <path d="M92 46 L112 62" strokeWidth="4.5" />
        <path d="M112 62 L132 90" strokeWidth="3.5" />
        <path d="M126 97 L142 93" strokeWidth="3.5" />
      </g>
    </svg>
  );
}

const CATEGORIES = [
  { key: "vi", Svg: ViSvg, accent: "sky", to: "/classi-visually-impaired" },
  { key: "standing", Svg: StandingSvg, accent: "emerald", to: null },
  { key: "sitting", Svg: SittingSvg, accent: "violet", to: null },
];

const ACCENT = {
  sky: { border: "hover:border-sky-400/60", text: "text-sky-400", ring: "group-hover:shadow-[0_0_40px_rgba(56,189,248,0.12)]" },
  emerald: { border: "hover:border-emerald-400/60", text: "text-emerald-400", ring: "group-hover:shadow-[0_0_40px_rgba(52,211,153,0.12)]" },
  violet: { border: "hover:border-violet-400/60", text: "text-violet-400", ring: "group-hover:shadow-[0_0_40px_rgba(167,139,250,0.12)]" },
};

function CategoryCard({ cat, index, featured }) {
  const { t } = useLang();
  const a = ACCENT[cat.accent];
  const inner = (
    <>
      <span className={`block ${featured ? "px-6 pt-6 h-44 sm:h-52" : "px-3 pt-3 h-16 md:px-6 md:pt-6 md:h-32"}`}>
        <cat.Svg />
      </span>
      <span className={`block pt-1 ${featured ? "px-6 pb-6 sm:pt-2" : "px-3 pb-3 md:px-6 md:pb-6 md:pt-2"}`}>
        <span className={`font-display block text-slate-50 ${featured ? "text-3xl sm:text-4xl" : "text-base md:text-xl lg:text-2xl"}`}>
          {t(`comeFunziona.cats.${cat.key}.title`)}
        </span>
        <span className={`mt-1 block font-bold uppercase tracking-[0.2em] ${a.text} ${featured ? "text-[10px]" : "text-[8px] md:text-[10px]"}`}>
          {t(`comeFunziona.cats.${cat.key}.tag`)}
        </span>
        <span className={`mt-3 block text-slate-400 leading-relaxed ${featured ? "text-xs sm:text-sm" : "text-[11px] md:text-xs line-clamp-3 md:line-clamp-none"}`}>
          {t(`comeFunziona.cats.${cat.key}.text`)}
        </span>
        {cat.to && (
          <span className={`mt-4 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] ${a.text}`}>
            {t("comeFunziona.cta")}
            <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
          </span>
        )}
      </span>
    </>
  );
  const base = "group relative block h-full overflow-hidden rounded-2xl border backdrop-blur-md text-left transition-all duration-300";
  const cls = featured
    ? `${base} border-sky-400/50 bg-sky-400/[0.06] shadow-[0_0_40px_rgba(56,189,248,0.10)] ${a.border} ${a.ring}`
    : `${base} border-slate-800/80 bg-slate-900/40 ${a.border} ${a.ring}`;

  return (
    <Reveal delay={0.08 * index} y={24} className="h-full">
      {cat.to ? (
        <Link to={cat.to} data-testid={`cat-card-${cat.key}`} className={cls}>{inner}</Link>
      ) : (
        <div data-testid={`cat-card-${cat.key}`} className={cls}>{inner}</div>
      )}
    </Reveal>
  );
}

export default function ComeFunziona() {
  const { t } = useLang();
  return (
    <section id="come-funziona" className="py-14 sm:py-28 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(56,189,248,0.05),transparent_55%)]" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <Reveal className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400">{t("comeFunziona.eyebrow")}</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-slate-50">
            {t("comeFunziona.titleA")}<span className="text-amber-400">{t("comeFunziona.titleAccent")}</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">{t("comeFunziona.subtitle")}</p>
        </Reveal>
        <div className="mt-10 grid grid-cols-2 md:grid-cols-2 lg:grid-cols-7 gap-3 sm:gap-5" data-testid="come-funziona-grid">
          <div className="col-span-2 md:col-span-2 lg:col-span-3">
            <CategoryCard cat={CATEGORIES[0]} index={0} featured />
          </div>
          <div className="lg:col-span-2">
            <CategoryCard cat={CATEGORIES[1]} index={1} />
          </div>
          <div className="lg:col-span-2">
            <CategoryCard cat={CATEGORIES[2]} index={2} />
          </div>
        </div>
      </div>
    </section>
  );
}
